/**
 * Agent-Knowledge Integration Interface
 * Test and manage how AI agents access and use knowledge base content
 * Simple interface for users to see agent-knowledge relationships and test enhancements
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Progress } from '../ui/Progress';
import { 
  Brain, Zap, TestTube, MessageSquare, Eye, 
  CheckCircle2, AlertCircle, Users, Settings,
  Play, RotateCcw, Download, Share2
} from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  type: 'content' | 'strategy' | 'research' | 'creative' | 'technical';
  description: string;
  knowledgeAccess: boolean;
  knowledgeTypes: string[];
  enhancementLevel: number; // 0-100
  lastUsed: Date;
  contentGenerated: number;
  qualityImprovement: number;
}

interface KnowledgeEnhancementTest {
  id: string;
  agentId: string;
  prompt: string;
  enhancedPrompt?: string;
  knowledgeUsed: string[];
  baselineOutput?: string;
  enhancedOutput?: string;
  improvementScore?: number;
  timestamp: Date;
  status: 'draft' | 'running' | 'completed' | 'failed';
}

interface AgentKnowledgeStats {
  totalAgents: number;
  knowledgeEnabledAgents: number;
  avgQualityImprovement: number;
  totalEnhancedContent: number;
  knowledgeUtilizationRate: number;
}

interface AgentKnowledgeIntegrationProps {
  organizationId: string;
  onTestComplete?: (test: KnowledgeEnhancementTest) => void;
}

export const AgentKnowledgeIntegration: React.FC<AgentKnowledgeIntegrationProps> = ({
  organizationId,
  onTestComplete
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tests, setTests] = useState<KnowledgeEnhancementTest[]>([]);
  const [stats, setStats] = useState<AgentKnowledgeStats>({
    totalAgents: 0,
    knowledgeEnabledAgents: 0,
    avgQualityImprovement: 0,
    totalEnhancedContent: 0,
    knowledgeUtilizationRate: 0
  });
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [testPrompt, setTestPrompt] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'agents' | 'testing' | 'results'>('overview');
  const [runningTest, setRunningTest] = useState(false);

  useEffect(() => {
    loadAgentData();
    loadTestResults();
  }, [organizationId]);

  const loadAgentData = async () => {
    // Mock data - replace with actual API calls
    const mockAgents: Agent[] = [
      {
        id: 'casey',
        name: 'Casey Email Agent',
        type: 'content',
        description: 'Generates professional email content with personalized messaging',
        knowledgeAccess: true,
        knowledgeTypes: ['Email Templates', 'Brand Guidelines', 'Product Info'],
        enhancementLevel: 87,
        lastUsed: new Date(Date.now() - 3600000),
        contentGenerated: 234,
        qualityImprovement: 43
      },
      {
        id: 'compass',
        name: 'Compass Strategy Agent',
        type: 'strategy',
        description: 'Strategic analysis and competitive positioning insights',
        knowledgeAccess: true,
        knowledgeTypes: ['Market Research', 'Competitive Analysis', 'Strategic Plans'],
        enhancementLevel: 92,
        lastUsed: new Date(Date.now() - 1800000),
        contentGenerated: 156,
        qualityImprovement: 61
      },
      {
        id: 'aria',
        name: 'Aria Content Editor',
        type: 'content',
        description: 'Content optimization and quality enhancement',
        knowledgeAccess: true,
        knowledgeTypes: ['Style Guides', 'Content Standards', 'Performance Data'],
        enhancementLevel: 78,
        lastUsed: new Date(Date.now() - 7200000),
        contentGenerated: 189,
        qualityImprovement: 35
      },
      {
        id: 'atlas',
        name: 'Atlas Knowledge Graph',
        type: 'research',
        description: 'Knowledge graph management and intelligent insights',
        knowledgeAccess: true,
        knowledgeTypes: ['All Documents', 'Relationship Data', 'Entity Mappings'],
        enhancementLevel: 96,
        lastUsed: new Date(Date.now() - 900000),
        contentGenerated: 312,
        qualityImprovement: 78
      },
      {
        id: 'brain',
        name: 'Brain Discovery Engine',
        type: 'research',
        description: 'Unknown-unknown discovery and pattern analysis',
        knowledgeAccess: true,
        knowledgeTypes: ['Cross-Document Patterns', 'Hidden Insights', 'Unknown Connections'],
        enhancementLevel: 89,
        lastUsed: new Date(Date.now() - 5400000),
        contentGenerated: 98,
        qualityImprovement: 52
      },
      {
        id: 'riley',
        name: 'Riley Research Agent',
        type: 'research',
        description: 'Market intelligence and research coordination',
        knowledgeAccess: false,
        knowledgeTypes: [],
        enhancementLevel: 0,
        lastUsed: new Date(Date.now() - 86400000),
        contentGenerated: 67,
        qualityImprovement: 0
      }
    ];

    setAgents(mockAgents);

    // Calculate stats
    const knowledgeEnabled = mockAgents.filter(a => a.knowledgeAccess);
    setStats({
      totalAgents: mockAgents.length,
      knowledgeEnabledAgents: knowledgeEnabled.length,
      avgQualityImprovement: Math.round(knowledgeEnabled.reduce((sum, a) => sum + a.qualityImprovement, 0) / knowledgeEnabled.length),
      totalEnhancedContent: mockAgents.reduce((sum, a) => sum + a.contentGenerated, 0),
      knowledgeUtilizationRate: Math.round((knowledgeEnabled.length / mockAgents.length) * 100)
    });
  };

  const loadTestResults = async () => {
    // Mock test results
    const mockTests: KnowledgeEnhancementTest[] = [
      {
        id: 'test_1',
        agentId: 'casey',
        prompt: 'Write a professional email introducing our new product features',
        knowledgeUsed: ['Product Feature Guide', 'Email Templates', 'Brand Voice Guidelines'],
        improvementScore: 87,
        timestamp: new Date(Date.now() - 3600000),
        status: 'completed'
      },
      {
        id: 'test_2',
        agentId: 'compass',
        prompt: 'Analyze competitive positioning for Q1 strategy',
        knowledgeUsed: ['Competitor Analysis Reports', 'Market Research', 'Positioning Framework'],
        improvementScore: 92,
        timestamp: new Date(Date.now() - 7200000),
        status: 'completed'
      }
    ];

    setTests(mockTests);
  };

  const runEnhancementTest = async () => {
    if (!selectedAgent || !testPrompt.trim()) return;

    setRunningTest(true);
    
    const newTest: KnowledgeEnhancementTest = {
      id: `test_${Date.now()}`,
      agentId: selectedAgent,
      prompt: testPrompt,
      knowledgeUsed: [],
      timestamp: new Date(),
      status: 'running'
    };

    setTests(prev => [newTest, ...prev]);

    // Simulate test execution
    setTimeout(() => {
      const agent = agents.find(a => a.id === selectedAgent);
      const completedTest = {
        ...newTest,
        status: 'completed' as const,
        knowledgeUsed: agent?.knowledgeTypes || [],
        improvementScore: Math.floor(Math.random() * 40) + 60,
        baselineOutput: 'Basic output without knowledge enhancement...',
        enhancedOutput: 'Enhanced output incorporating relevant knowledge from uploaded documents...'
      };

      setTests(prev => prev.map(t => t.id === newTest.id ? completedTest : t));
      setRunningTest(false);
      onTestComplete?.(completedTest);
    }, 3000);
  };

  const getAgentTypeColor = (type: Agent['type']) => {
    switch (type) {
      case 'content': return 'bg-blue-100 text-blue-800';
      case 'strategy': return 'bg-purple-100 text-purple-800';
      case 'research': return 'bg-green-100 text-green-800';
      case 'creative': return 'bg-pink-100 text-pink-800';
      case 'technical': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-600">{stats.totalAgents}</div>
              <div className="text-sm text-gray-600">Total Agents</div>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.knowledgeEnabledAgents}</div>
              <div className="text-sm text-gray-600">Knowledge Enabled</div>
            </div>
            <Brain className="h-8 w-8 text-green-600" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-purple-600">{stats.avgQualityImprovement}%</div>
              <div className="text-sm text-gray-600">Avg Quality Boost</div>
            </div>
            <Zap className="h-8 w-8 text-purple-600" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-orange-600">{stats.totalEnhancedContent}</div>
              <div className="text-sm text-gray-600">Enhanced Content</div>
            </div>
            <MessageSquare className="h-8 w-8 text-orange-600" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAgents = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Agent Knowledge Integration Status</h2>
        <div className="text-sm text-gray-600">
          {stats.knowledgeUtilizationRate}% of agents using knowledge base
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {agents.map(agent => (
          <Card key={agent.id} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{agent.name}</h3>
                    <Badge className={getAgentTypeColor(agent.type)}>
                      {agent.type}
                    </Badge>
                    {agent.knowledgeAccess ? (
                      <Badge className="bg-green-100 text-green-800">
                        <Brain className="h-3 w-3 mr-1" />
                        Knowledge Enabled
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        Not Connected
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3">{agent.description}</p>
                  
                  {agent.knowledgeAccess && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">Knowledge Enhancement Level</span>
                        <span className="text-gray-600">{agent.enhancementLevel}%</span>
                      </div>
                      <Progress value={agent.enhancementLevel} className="h-2" />
                      
                      <div className="grid grid-cols-3 gap-4 text-sm mt-3">
                        <div>
                          <span className="font-medium">Content Generated:</span> {agent.contentGenerated}
                        </div>
                        <div>
                          <span className="font-medium">Quality Improvement:</span> +{agent.qualityImprovement}%
                        </div>
                        <div>
                          <span className="font-medium">Last Used:</span> {agent.lastUsed.toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <span className="text-sm font-medium">Knowledge Types:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {agent.knowledgeTypes.map(type => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    size="small"
                    variant="secondary"
                    onClick={() => {
                      setSelectedAgent(agent.id);
                      setActiveTab('testing');
                    }}
                  >
                    <TestTube className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderTesting = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4">Test Knowledge Enhancement</h2>
        
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Agent</label>
                <select
                  value={selectedAgent || ''}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose an agent to test...</option>
                  {agents.filter(a => a.knowledgeAccess).map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Test Prompt</label>
                <textarea
                  placeholder="Enter a prompt to test how knowledge enhancement improves the agent's output..."
                  value={testPrompt}
                  onChange={(e) => setTestPrompt(e.target.value)}
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex gap-4">
                <Button 
                  onClick={runEnhancementTest}
                  disabled={!selectedAgent || !testPrompt.trim() || runningTest}
                >
                  {runningTest ? (
                    <>
                      <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run Test
                    </>
                  )}
                </Button>
                
                <Button variant="secondary" onClick={() => {
                  setTestPrompt('');
                  setSelectedAgent(null);
                }}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {selectedAgent && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Agent Information</h3>
          <Card>
            <CardContent className="p-4">
              {(() => {
                const agent = agents.find(a => a.id === selectedAgent);
                if (!agent) return null;
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Agent:</span> {agent.name}
                    </div>
                    <div>
                      <span className="font-medium">Enhancement Level:</span> {agent.enhancementLevel}%
                    </div>
                    <div className="md:col-span-2">
                      <span className="font-medium">Available Knowledge:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {agent.knowledgeTypes.map(type => (
                          <Badge key={type} variant="outline" className="text-xs">
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  const renderResults = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Test Results</h2>
        <div className="text-sm text-gray-600">
          {tests.length} tests performed
        </div>
      </div>
      
      <div className="space-y-4">
        {tests.map(test => (
          <Card key={test.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold">
                      {agents.find(a => a.id === test.agentId)?.name}
                    </h3>
                    <Badge variant={test.status === 'completed' ? 'default' : 'secondary'}>
                      {test.status}
                    </Badge>
                    {test.improvementScore && (
                      <Badge className="bg-green-100 text-green-800">
                        +{test.improvementScore}% improvement
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-gray-800 mb-3">{test.prompt}</p>
                  
                  {test.knowledgeUsed.length > 0 && (
                    <div className="mb-3">
                      <span className="text-sm font-medium">Knowledge Used:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {test.knowledgeUsed.map(knowledge => (
                          <Badge key={knowledge} variant="outline" className="text-xs">
                            {knowledge}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Tested: {test.timestamp.toLocaleDateString()} at {test.timestamp.toLocaleTimeString()}
                  </div>
                </div>
                
                {test.status === 'completed' && (
                  <div className="ml-4">
                    <Button size="small" variant="secondary">
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                  </div>
                )}
              </div>
              
              {test.status === 'running' && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <RotateCcw className="h-4 w-4 animate-spin" />
                    Testing knowledge enhancement...
                  </div>
                  <Progress value={66} className="h-2 mt-2" />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {tests.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <TestTube className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No tests yet</h3>
              <p className="text-gray-600 mb-4">
                Run your first knowledge enhancement test to see how agents improve with access to your knowledge base.
              </p>
              <Button onClick={() => setActiveTab('testing')}>
                <Play className="h-4 w-4 mr-2" />
                Start Testing
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Agent-Knowledge Integration</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Utilization: <span className="font-semibold text-green-600">{stats.knowledgeUtilizationRate}%</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-2 mb-6 border-b">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'agents', label: 'Agents', icon: Users },
          { id: 'testing', label: 'Test Enhancement', icon: TestTube },
          { id: 'results', label: 'Results', icon: CheckCircle2 }
        ].map(tab => (
          <button
            key={tab.id}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab(tab.id as any)}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'agents' && renderAgents()}
      {activeTab === 'testing' && renderTesting()}
      {activeTab === 'results' && renderResults()}
    </div>
  );
};

export default AgentKnowledgeIntegration;